import type { MetricSnapshot } from '../types';
import { formatMetric } from '../lib/format';

interface MetricTrendChartProps {
  snapshots: MetricSnapshot[];
  metric: string;
  aliases?: string[];
  label: string;
  color?: string;
  maxPoints?: number;
}

interface TrendPoint {
  date: string;
  value: number;
}

export function trendPoints(
  snapshots: MetricSnapshot[],
  metric: string,
  aliases: string[] = [],
  maxPoints = 18
): TrendPoint[] {
  const keys = [metric, ...aliases];
  return snapshots
    .map((snapshot) => {
      const value = keys.map((key) => snapshot.metrics[key]).find((candidate) => typeof candidate === 'number');
      return typeof value === 'number'
        ? { date: snapshot.windowEnd || snapshot.recordedAt, value }
        : null;
    })
    .filter((point): point is TrendPoint => point !== null)
    .sort((left, right) => left.date.localeCompare(right.date))
    .slice(-maxPoints);
}

export function MetricTrendChart({
  snapshots,
  metric,
  aliases = [],
  label,
  color = '#f55753',
  maxPoints = 18
}: MetricTrendChartProps) {
  const points = trendPoints(snapshots, metric, aliases, maxPoints);
  const latest = points.length ? points[points.length - 1].value : null;
  const previous = points.length > 1 ? points[points.length - 2].value : null;
  const delta = latest !== null && previous !== null ? latest - previous : null;
  const width = 360;
  const height = 116;
  const pad = 10;
  const values = points.map((point) => point.value);
  const minimum = values.length ? Math.min(...values) : 0;
  const maximum = values.length ? Math.max(...values) : 0;
  const range = maximum - minimum || 1;
  const path = points.map((point, index) => {
    const x = points.length === 1 ? width / 2 : pad + index * ((width - pad * 2) / (points.length - 1));
    const y = height - pad - ((point.value - minimum) / range) * (height - pad * 2);
    return `${index ? 'L' : 'M'} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
  const dateRange = points.length
    ? `${new Date(points[0].date).toLocaleDateString('en-IE', { month: 'short', day: 'numeric' })} – ${new Date(points[points.length - 1].date).toLocaleDateString('en-IE', { month: 'short', day: 'numeric' })}`
    : 'No observations';

  return (
    <article className="trend-card">
      <div className="trend-card__header">
        <div><span>{label}</span><strong>{formatMetric(metric, latest)}</strong></div>
        {delta !== null ? <small className={delta > 0 ? 'trend-up' : delta < 0 ? 'trend-down' : ''}>{delta > 0 ? '+' : ''}{formatMetric(metric, delta)} vs prior</small> : <small>Awaiting comparison</small>}
      </div>
      {points.length ? (
        <svg className="trend-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${label} trend, ${points.length} observations, latest ${formatMetric(metric, latest)}`} preserveAspectRatio="none">
          <line x1={pad} x2={width - pad} y1={height - pad} y2={height - pad} className="trend-chart__axis" />
          {path ? <path d={path} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" /> : null}
          {points.length === 1 ? <circle cx={width / 2} cy={height - pad} r="5" fill={color} /> : null}
        </svg>
      ) : <div className="trend-chart trend-chart--empty">No evidence yet</div>}
      <footer><span>{dateRange}</span><span>{points.length} observation{points.length === 1 ? '' : 's'}</span></footer>
    </article>
  );
}
